import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ZagIcon } from '../icon/icon';

/**
 * Color variant for stat items with icons
 */
export type StatItemColor = 'primary' | 'success' | 'warning' | 'error' | 'info';

/**
 * Individual stat item configuration.
 *
 * Supports two display modes:
 * 1. **Simple mode** (with icon): Shows icon + label + value, used for dashboard KPI cards
 * 2. **List mode** (with percentage): Shows label + value + progress bar + meta text
 */
export interface StatItem {
  /** Stable identifier — used by consumers to map a clicked tile back to domain data. */
  id?: string;

  /** Display label for the stat */
  label: string;

  /** Display value (pre-formatted string) */
  value: string;

  /**
   * Material icon name for simple mode.
   * When provided, displays the stat as an icon card.
   */
  icon?: string;

  /**
   * Color variant for the icon container.
   * Only used when `icon` is provided.
   * @default 'primary'
   */
  color?: StatItemColor;

  /**
   * Trend percentage for simple mode.
   * Positive = up arrow (green), Negative = down arrow (red)
   */
  trend?: number;

  /**
   * Trend comparison label (e.g., "vs last week")
   * @default 'vs last week'
   */
  trendLabel?: string;

  /**
   * Progress bar percentage (0-100) for list mode.
   * When provided, displays a progress bar below the value.
   */
  percentage?: number;

  /**
   * Additional meta text displayed below the bar in list mode.
   */
  meta?: string;

  /**
   * Progress bar color variant for list mode.
   * @default 'success'
   */
  variant?: 'success' | 'warning' | 'error' | 'info';

  /**
   * Router link for simple-mode cards that should navigate somewhere (e.g. a
   * KPI tile linking to its drill-down page). When set, the card renders as
   * a link instead of a static card.
   */
  route?: string[];

  /** CTA label rendered as link footer on clickable cards. Hidden when unset. */
  cta?: string;
}

/**
 * Display mode for the stats widget
 */
export type StatsWidgetMode = 'auto' | 'simple' | 'list';

/**
 * StatsWidget Component
 *
 * A flexible component for displaying statistics in different formats.
 * Automatically detects the display mode based on the stat item properties,
 * or can be explicitly set via the `mode` input.
 *
 * ## Simple Mode (Icon Cards)
 * Used for dashboard KPI displays. Shows icon + label + value with optional trend.
 *
 * @example
 * ```html
 * <zag-stats-widget [stats]="[
 *   { icon: 'group', label: 'Active Users', value: '1,234', color: 'primary' },
 *   { icon: 'trending_up', label: 'Growth', value: '+15%', color: 'success', trend: 15 }
 * ]" />
 * ```
 *
 * ## List Mode (Progress Bars)
 * Used for showing metrics with progress indicators.
 *
 * @example
 * ```html
 * <zag-stats-widget [stats]="[
 *   { label: 'Coverage', value: '85%', percentage: 85, variant: 'success' },
 *   { label: 'Utilization', value: '62%', percentage: 62, variant: 'warning', meta: 'Target: 75%' }
 * ]" />
 * ```
 */
@Component({
  selector: 'zag-stats-widget',
  imports: [ZagIcon, RouterLink],
  template: `
    <div
      class="zag-stats-widget__container"
      [class.zag-stats-widget__container--simple]="isSimpleMode()"
      [class.zag-stats-widget__container--list]="!isSimpleMode()"
    >
      @for (stat of stats(); track stat.label) {
        @if (isSimpleItem(stat)) {
          <!-- Simple Mode: Icon Card -->
          @if (stat.route) {
            <a class="zag-stats-widget__card zag-stats-widget__card--link" [routerLink]="stat.route">
              <div class="zag-stats-widget__icon-container" [class]="getIconContainerClass(stat)">
                <span [zagIcon]="stat.icon!" class="zag-stats-widget__icon"></span>
              </div>
              <div class="zag-stats-widget__card-content">
                <div class="zag-stats-widget__card-label">{{ stat.label }}</div>
                <div class="zag-stats-widget__card-value">{{ stat.value }}</div>
                @if (stat.trend !== undefined && stat.trend !== null) {
                  <div class="zag-stats-widget__trend" [class]="getTrendClass(stat.trend)">
                    <span [zagIcon]="'trending_up'" class="zag-stats-widget__trend-icon"></span>
                    {{ formatTrend(stat.trend, stat.trendLabel) }}
                  </div>
                }
                @if (stat.cta) {
                  <div class="zag-stats-widget__card-cta">
                    {{ stat.cta }}
                    <span zagIcon="arrow_forward"></span>
                  </div>
                }
              </div>
            </a>
          } @else if (clickable()) {
            <div
              class="zag-stats-widget__card zag-stats-widget__card--link"
              role="button"
              tabindex="0"
              (click)="onTileClick(stat)"
              (keydown)="onTileKeydown($event, stat)"
            >
              <div class="zag-stats-widget__icon-container" [class]="getIconContainerClass(stat)">
                <span [zagIcon]="stat.icon!" class="zag-stats-widget__icon"></span>
              </div>
              <div class="zag-stats-widget__card-content">
                <div class="zag-stats-widget__card-label">{{ stat.label }}</div>
                <div class="zag-stats-widget__card-value">{{ stat.value }}</div>
                @if (stat.trend !== undefined && stat.trend !== null) {
                  <div class="zag-stats-widget__trend" [class]="getTrendClass(stat.trend)">
                    <span [zagIcon]="'trending_up'" class="zag-stats-widget__trend-icon"></span>
                    {{ formatTrend(stat.trend, stat.trendLabel) }}
                  </div>
                }
                @if (stat.cta) {
                  <div class="zag-stats-widget__card-cta">
                    {{ stat.cta }}
                    <span zagIcon="arrow_forward"></span>
                  </div>
                }
              </div>
            </div>
          } @else {
            <div class="zag-stats-widget__card">
              <div class="zag-stats-widget__icon-container" [class]="getIconContainerClass(stat)">
                <span [zagIcon]="stat.icon!" class="zag-stats-widget__icon"></span>
              </div>
              <div class="zag-stats-widget__card-content">
                <div class="zag-stats-widget__card-label">{{ stat.label }}</div>
                <div class="zag-stats-widget__card-value">{{ stat.value }}</div>
                @if (stat.trend !== undefined && stat.trend !== null) {
                  <div class="zag-stats-widget__trend" [class]="getTrendClass(stat.trend)">
                    <span [zagIcon]="'trending_up'" class="zag-stats-widget__trend-icon"></span>
                    {{ formatTrend(stat.trend, stat.trendLabel) }}
                  </div>
                }
              </div>
            </div>
          }
        } @else {
          <!-- List Mode: Progress Bar -->
          <div class="zag-stats-widget__item">
            <div class="zag-stats-widget__label">{{ stat.label }}</div>
            <div class="zag-stats-widget__value">{{ stat.value }}</div>
            @if (stat.percentage !== undefined) {
              <div class="zag-stats-widget__bar">
                <div class="zag-stats-widget__bar-fill" [class]="getBarFillClass(stat)" [style.width.%]="stat.percentage"></div>
              </div>
            }
            @if (stat.meta) {
              <div class="zag-stats-widget__meta">{{ stat.meta }}</div>
            }
          </div>
        }
      }
    </div>
  `,
  styleUrl: './stats-widget.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.zag-stats-widget]': 'true',
  },
})
export class StatsWidget {
  /**
   * Array of statistics to display.
   */
  readonly stats = input<StatItem[]>([]);

  /**
   * Display mode for the widget.
   * - `auto`: Automatically detect based on first item's properties (default)
   * - `simple`: Force icon card display
   * - `list`: Force progress bar display
   */
  readonly mode = input<StatsWidgetMode>('auto');

  /**
   * Number of columns for grid layout (simple mode only).
   * @default 4
   */
  readonly columns = input<number>(4);

  /**
   * When true, simple-mode cards (without a `route`) render as role-button cards and
   * emit `tileClick` on interaction. Used for KPI drill-down tiles.
   */
  readonly clickable = input<boolean>(false);

  readonly tileClick = output<StatItem>();

  /**
   * Computed: Whether to use simple mode (icon cards)
   */
  protected readonly isSimpleMode = computed(() => {
    const m = this.mode();
    if (m === 'simple') return true;
    if (m === 'list') return false;

    // Auto-detect: check if first item has an icon
    const items = this.stats();
    return items.length > 0 && !!items[0].icon;
  });

  /**
   * Check if a stat item should render as simple (icon card) mode
   */
  protected isSimpleItem(stat: StatItem): boolean {
    return !!stat.icon;
  }

  /**
   * Get CSS class for icon container based on color
   */
  protected getIconContainerClass(stat: StatItem): string {
    const color = stat.color ?? 'primary';
    return `zag-stats-widget__icon-container--${color}`;
  }

  /**
   * Get CSS class for trend indicator
   */
  protected getTrendClass(trend: number): string {
    return trend >= 0 ? 'zag-stats-widget__trend--positive' : 'zag-stats-widget__trend--negative';
  }

  /**
   * Format trend display text
   */
  protected formatTrend(trend: number, label?: string): string {
    const absValue = Math.abs(trend);
    return `${absValue}% ${label ?? 'vs last week'}`;
  }

  /**
   * Get CSS class for progress bar fill based on variant
   */
  protected getBarFillClass(stat: StatItem): string {
    const variant = stat.variant ?? 'success';
    return `zag-stats-widget__bar-fill--${variant}`;
  }

  protected onTileClick(stat: StatItem): void {
    this.tileClick.emit(stat);
  }

  protected onTileKeydown(event: KeyboardEvent, stat: StatItem): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.onTileClick(stat);
  }
}
